import { useEffect, useRef, useState } from "react";
import { NavLink, useParams } from "react-router-dom";
import { fromUnixTime, getUnixTime } from "date-fns";
import {
  CheckCircle2Icon,
  CircleDashedIcon,
  CircleIcon,
  DownloadIcon,
  FileTextIcon,
  FileUpIcon,
  FolderPlusIcon,
  PanelRightCloseIcon,
  PlusIcon,
  UserPlusIcon
} from "lucide-react";

import { Avatar } from "../../../components/account/Avatar";
import { AnimatedModal } from "../../../components/AnimatedModal";
import { AddFolderPill } from "../../../components/AddFolderPill";
import { Button } from "../../../components/tailwind/Button";
import { Checkbox } from "../../../components/checkbox";
import { CloseIcon } from "../../../components/CloseIcon";
import { TextArea } from "../../../components/tailwind/TextArea";
import { TextInput } from "../../../components/tailwind/TextInput";
import Dropdown, { Option } from "../../../components/tailwind/Dropdown";
import { Pills } from "../../../components/Pills";
import { UserPill } from "../../../components/UserPill";
import { SelectUserPill } from "../../../components/SelectUserPill";
import { SelectTaskPriority } from "../../../components/SelectTaskPriorityPill";
import { TaskPriorityIcon } from "../../../components/TaskPriorityPill";
import { DatePicker } from "../../../components/DatePicker";
import { toasts } from "../../../components/toasts/toasts";
import { classNames } from "../../../utils/cn";
import useGqlClient from "../../../hooks/useGqlClient";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Account,
  CreateTaskFileInput,
  CreateTaskFileResponse,
  DataRoomFolder,
  DealActivityType,
  DealActivityFragmentFragment,
  FileType,
  S3MultipartTaskFileUploadCompleteInput,
  Task,
  TaskFile,
  TaskFileUploadStatus,
  TaskPriority,
  TaskQuery,
  TaskStatus,
  useCreateTaskMutation,
  useDataRoomQuery,
  useTaskQuery,
  useUpdateTaskMutation,
} from "../../../graphql/generated";
import { getNumberOfParts, Uploader } from "../../../utils/multipartUploader";
import { Spinner } from "../../../components/icons/Spinner";
import { SlideOver } from "../../../components/SlideOver";
import DocViewer, { DocViewerRenderers } from "@cyntler/react-doc-viewer";
import SyncfusionPdfViewer from "../../../components/document_viewers/SyncfusionPdfViewer";
import { formatFileSize, fileNameWithoutExtension } from "../../../utils/formatFileSize";
import { v4 as uuid } from "uuid";
import { LocalFileIcon } from "../../../components/icons/LocalFileIcon";
import { BackButton } from "@/src/components/BackButton";
import { ActivityItems } from "../../../components/activity/ActivityItems";
import { TaskCommentInput } from "../../tasks/TaskCommentInput";

const TASK_ACTIVITY_TYPES = [
  DealActivityType.TaskCreated,
  DealActivityType.TaskUpdated,
  DealActivityType.TaskAssigned,
  DealActivityType.TaskDueDate,
  DealActivityType.TaskStatusUpdated,
  DealActivityType.TaskPriorityUpdated,
  DealActivityType.Comment,
];

function isTaskCommentActivity(type: DealActivityType) {
  return type === DealActivityType.Comment;
}

function isSidebarTaskActivity(type: DealActivityType) {
  return TASK_ACTIVITY_TYPES.includes(type) && !isTaskCommentActivity(type);
}

const TaskFilesDocument = `
  query TaskFiles($id: String!) {
    task(id: $id) {
      id
      files {
        id
        fileName
        fileType
        fileSize
        uploadStatus
        viewUrl
        downloadUrl
      }
    }
  }
`;

const CreateTaskFileDocument = `
  mutation CreateTaskFile($input: CreateTaskFileInput!) {
    createTaskFile(input: $input) {
      multiPartUploadID
      parts {
        partNumber
        presignedUrl
      }
      taskFile {
        id
        fileName
        fileType
        fileSize
        uploadStatus
        viewUrl
        downloadUrl
      }
    }
  }
`;

const S3MultipartTaskFileUploadCompleteDocument = `
  mutation S3MultipartTaskFileUploadComplete(
    $input: S3MultipartTaskFileUploadCompleteInput!
  ) {
    s3MultipartTaskFileUploadComplete(input: $input) {
      id
      fileName
      fileType
      fileSize
      uploadStatus
      viewUrl
      downloadUrl
    }
  }
`;

const AddTaskFilesToDataRoomDocument = `
  mutation AddTaskFilesToDataRoom(
    $dealID: String!
    $dataRoomFolderID: String!
    $dataRoomPermissionID: String!
    $taskFileIDs: [String!]!
  ) {
    addTaskFilesToDataRoom(
      dealID: $dealID
      dataRoomFolderID: $dataRoomFolderID
      dataRoomPermissionID: $dataRoomPermissionID
      taskFileIDs: $taskFileIDs
    ) {
      id
    }
  }
`;

type TaskFilesQuery = {
  task: {
    id: string;
    files: TaskFile[];
  };
};

type TaskFilesQueryVariables = {
  id: string;
};

export function DealTaskDetails() {
  const { dealId, taskId } = useParams<{ dealId: string; taskId: string }>();
  const client = useGqlClient();
  const queryClient = useQueryClient();
  const [isEditOpen, setIsEditOpen] = useState(false);

  const taskQuery = useTaskQuery(client, { id: taskId });
  const taskFilesQuery = useQuery({
    queryKey: ["TaskFiles", { id: taskId }],
    queryFn: () =>
      client.request<TaskFilesQuery, TaskFilesQueryVariables>(
        TaskFilesDocument,
        {
          id: taskId ?? "",
        }
      ),
    enabled: Boolean(taskId),
  });

  const refreshTask = () => {
    queryClient.invalidateQueries({ queryKey: ["Task", { id: taskId }] });
    queryClient.invalidateQueries({ queryKey: ["TaskFiles", { id: taskId }] });
  };

  if (taskQuery.isLoading || !taskQuery.data) {
    return <TaskDetailsSkeleton />;
  }

  if (taskQuery.error) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <p className="text-gray-500 font-medium">Failed to load task</p>
        <Button
          variant="neutral"
          size="s"
          text="Retry"
          onClick={() => taskQuery.refetch()}
          margin="m 0 0 0"
        />
      </div>
    );
  }

  const task = taskQuery.data.task;

  return (
    <div className="max-h-[calc(100vh-130px)] flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-x-3 px-4 py-3 border-b border-gray-100">
        <BackButton size="sm" />

        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-semibold text-gray-900 truncate">
            {task.title}
          </h1>
        </div>
        <Button
          variant="neutral"
          size="s"
          text="Edit"
          onClick={() => setIsEditOpen(true)}
        />
      </div>

      {/* Content */}
      <div className="flex flex-col lg:flex-row flex-1 min-h-0">
        {/* Main Content */}
        <div className="flex-1 p-4 lg:border-r border-gray-100 overflow-y-auto scrollbar-thin">
          <TaskDescription description={task.description ?? ""} />
          <TaskFilesSection
            taskId={taskId ?? ""}
            files={taskFilesQuery.data?.task.files ?? []}
            isLoading={taskFilesQuery.isLoading}
            onUpdated={refreshTask}
          />
          <SubTasksSection
            dealId={dealId ?? ""}
            taskId={taskId}
            subTasks={task.subTasks as Task[]}
            onUpdated={refreshTask}
          />

          <div className="mt-8">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Comments</h3>
            <ActivityTimeline
              activities={task.activity.filter(
                (a) => isTaskCommentActivity(a.type)
              )}
              defaultSortOrder="asc"
            />
            <TaskCommentInput
              input={{ taskId: task.id, comment: "", files: [] }}
              onCommented={refreshTask}
            />
          </div>
        </div>

        {/* Properties Sidebar */}
        <div className="w-full lg:w-96 p-4 bg-gray-50/50 no-scrollbar lg:overflow-y-auto">
          <PropertiesPanel task={task} onUpdated={refreshTask} />
        </div>
      </div>
      <EditTaskModal
        open={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        taskId={taskId}
        initialTitle={task.title}
        initialDescription={task.description ?? ""}
        onUpdated={() => {
          setIsEditOpen(false);
          refreshTask();
        }}
      />
    </div>
  );
}

function TaskDetailsSkeleton() {
  return (
    <div className="p-4 animate-pulse">
      <div className="flex items-center gap-x-3 mb-6">
        <div className="w-8 h-8 bg-gray-200 rounded" />
        <div className="h-6 bg-gray-200 rounded w-64" />
      </div>
      <div className="space-y-3">
        <div className="h-4 bg-gray-200 rounded w-full" />
        <div className="h-4 bg-gray-200 rounded w-3/4" />
        <div className="h-4 bg-gray-200 rounded w-1/2" />
      </div>
    </div>
  );
}

function TaskDescription({
  description,
}: {
  description: string;
}) {
  return (
    <div>
      <label className="text-sm font-semibold text-gray-700 mb-2 block">
        Description
      </label>
      {description.trim() === "" ? (
        <p className="text-sm text-gray-400">No description</p>
      ) : (
        <p className="text-sm text-gray-700 whitespace-pre-wrap">
          {description}
        </p>
      )}
    </div>
  );
}

type TaskFileUploadItem = {
  id: string;
  file: File;
  name: string;
  progress: number;
  status: "pending" | "creating" | "uploading" | "complete" | "error";
  taskFile?: TaskFile;
};

function TaskFilesSection({
  taskId,
  files,
  isLoading,
  onUpdated,
}: {
  taskId: string;
  files: TaskFile[];
  isLoading: boolean;
  onUpdated: () => void;
}) {
  const { dealId } = useParams<{ dealId: string }>();
  const client = useGqlClient();
  const queryClient = useQueryClient();
  const [dragActive, setDragActive] = useState(false);
  const [uploadItems, setUploadItems] = useState<TaskFileUploadItem[]>([]);
  const [activeFile, setActiveFile] = useState<TaskFile | null>(null);
  const [selectedFileIds, setSelectedFileIds] = useState<string[]>([]);
  const [isAddToDataRoomOpen, setIsAddToDataRoomOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const updateUploadItem = (
    id: string,
    updates: Partial<TaskFileUploadItem>
  ) => {
    setUploadItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, ...updates } : item))
    );
  };

  const uploadFile = async (item: TaskFileUploadItem) => {
    updateUploadItem(item.id, { status: "creating", progress: 0 });

    const input: CreateTaskFileInput = {
      fileName: item.file.name,
      fileType: item.file.type,
      parts: getNumberOfParts(item.file),
      taskID: taskId,
    };

    try {
      const createResponse = await client.request<
        { createTaskFile: CreateTaskFileResponse },
        { input: CreateTaskFileInput }
      >(CreateTaskFileDocument, {
        input,
      });

      updateUploadItem(item.id, {
        status: "uploading",
        taskFile: createResponse.createTaskFile.taskFile,
      });

      const uploader = new Uploader({
        file: item.file,
        uploadId: createResponse.createTaskFile.multiPartUploadID,
        parts: createResponse.createTaskFile.parts.map((part) => ({
          PartNumber: part.partNumber,
          signedUrl: part.presignedUrl,
        })),
      });

      uploader.start();

      uploader.onError(() => {
        updateUploadItem(item.id, { status: "error" });
      });

      uploader.onProgress((progress) => {
        updateUploadItem(item.id, {
          status: "uploading",
          progress: progress.percentage,
        });
      });

      uploader.onComplete(async (uploadedParts) => {
        try {
          const completeInput: S3MultipartTaskFileUploadCompleteInput = {
            taskFileID: createResponse.createTaskFile.taskFile.id,
            multiPartUploadID: createResponse.createTaskFile.multiPartUploadID,
            fileSize: item.file.size,
            parts: uploadedParts.map((part) => ({
              partNumber: part.PartNumber,
              etag: part.ETag,
            })),
          };

          const completeResponse = await client.request<
            { s3MultipartTaskFileUploadComplete: TaskFile },
            { input: S3MultipartTaskFileUploadCompleteInput }
          >(S3MultipartTaskFileUploadCompleteDocument, {
            input: completeInput,
          });

          updateUploadItem(item.id, {
            status: "complete",
            progress: 100,
            taskFile: completeResponse.s3MultipartTaskFileUploadComplete,
          });
          onUpdated();
        } catch (error) {
          updateUploadItem(item.id, { status: "error" });
          toasts.error("Failed to finalize upload");
        }
      });
    } catch (error) {
      updateUploadItem(item.id, { status: "error" });
      toasts.error("Failed to start upload");
    }
  };

  const handleFilesAdded = (filesList: FileList | null) => {
    if (!filesList || filesList.length === 0) {
      return;
    }

    const newItems = Array.from(filesList).map((file) => ({
      id: uuid(),
      file,
      name: fileNameWithoutExtension(file.name),
      progress: 0,
      status: "pending" as const,
    }));

    setUploadItems((prev) => [...newItems, ...prev]);
    newItems.forEach((item) => uploadFile(item));
  };

  const handleDrag = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.type === "dragenter" || event.type === "dragover") {
      setDragActive(true);
    } else if (event.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
    if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      handleFilesAdded(event.dataTransfer.files);
    }
  };

  const visibleUploadItems = uploadItems.filter((item) => {
    if (item.status !== "complete") {
      return true;
    }
    if (!item.taskFile) {
      return true;
    }
    return !files.some((file) => file.id === item.taskFile?.id);
  });

  useEffect(() => {
    const hasPendingFiles = files.some(
      (file) =>
        file.uploadStatus === TaskFileUploadStatus.Pending ||
        file.uploadStatus === TaskFileUploadStatus.Preparing
    );

    if (!hasPendingFiles) {
      return;
    }

    const intervalId = window.setInterval(() => {
      queryClient.invalidateQueries({
        queryKey: ["TaskFiles", { id: taskId }],
      });
    }, 4000);

    return () => window.clearInterval(intervalId);
  }, [files, queryClient, taskId]);

  useEffect(() => {
    setSelectedFileIds((prev) =>
      prev.filter((id) =>
        files.some(
          (file) =>
            file.id === id && file.uploadStatus === TaskFileUploadStatus.Ready
        )
      )
    );
  }, [files]);

  const selectedFiles = files.filter((file) =>
    selectedFileIds.includes(file.id)
  );

  const toggleFileSelection = (fileId: string) => {
    setSelectedFileIds((prev) =>
      prev.includes(fileId)
        ? prev.filter((id) => id !== fileId)
        : [...prev, fileId]
    );
  };

  return (
    <div className="mt-6">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-x-3">
          <label className="text-sm font-semibold text-gray-700">
            Attachments
          </label>
          <button
            type="button"
            onClick={() => {
              if (files.length === 0) {
                return;
              }
              const readyFiles = files.filter(
                (file) => file.uploadStatus === TaskFileUploadStatus.Ready
              );
              if (readyFiles.length === 0) {
                return;
              }
              const allReadySelected = readyFiles.every((file) =>
                selectedFileIds.includes(file.id)
              );
              setSelectedFileIds(
                allReadySelected ? [] : readyFiles.map((file) => file.id)
              );
            }}
            className="text-xs font-medium text-gray-500 hover:text-gray-700"
          >
            Select all
          </button>
        </div>
        <Button
          variant="neutral"
          size="s"
          text="Add to Data Room"
          onClick={() => setIsAddToDataRoomOpen(true)}
          icon={FolderPlusIcon}
          isDisabled={selectedFileIds.length === 0}
        />
      </div>
      <div className="flex flex-wrap gap-3 mb-3">
        {visibleUploadItems.map((item) => (
          <UploadFilePill
            key={item.id}
            item={item}
            onRetry={() => uploadFile(item)}
          />
        ))}
        {isLoading ? (
          <div className="flex items-center gap-x-2 text-sm text-gray-500">
            <Spinner color="gray" size="s" />
            Loading files...
          </div>
        ) : files.length === 0 && visibleUploadItems.length === 0 ? (
          <p className="text-sm text-gray-400">No files uploaded</p>
        ) : (
          files.map((file) => (
            <TaskFilePill
              key={file.id}
              file={file}
              onClick={() => setActiveFile(file)}
              selectionMode={selectedFileIds.length > 0}
              isSelected={selectedFileIds.includes(file.id)}
              isSelectable={file.uploadStatus === TaskFileUploadStatus.Ready}
              onSelectionChange={() => toggleFileSelection(file.id)}
            />
          ))
        )}
      </div>
      <div
        className={classNames(
          "p-3 rounded-md border-2 border-dashed bg-gray-50 transition-all duration-200 flex items-center justify-between",
          dragActive ? "border-persian-500 bg-persian-100/80 shadow" : "",
        )}
        onDragEnter={handleDrag}
        onDragOver={handleDrag}
        onDragLeave={handleDrag}
        onDrop={handleDrop}
      >
        <div className="flex items-center gap-x-2">
          <FileTextIcon className="w-4 h-4 text-gray-400" />
          <p className="text-sm text-gray-500">
            Drag and drop files here, or use the upload button.
          </p>
        </div>
        <Button
          variant="neutral"
          size="s"
          text="Upload"
          icon={FileUpIcon}
          onClick={() => {
            if (inputRef.current) {
              inputRef.current.click();
            }
          }}
        />
      </div>
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        multiple
        onChange={(event) => handleFilesAdded(event.target.files)}
      />
      <TaskFileSlideOver
        file={activeFile}
        open={Boolean(activeFile)}
        onClose={() => setActiveFile(null)}
      />
      <AddTaskFilesToDataRoomModal
        open={isAddToDataRoomOpen}
        onClose={() => setIsAddToDataRoomOpen(false)}
        selectedFiles={selectedFiles}
        onCompleted={() => {
          setIsAddToDataRoomOpen(false);
          setSelectedFileIds([]);
        }}
      />
    </div>
  );
}

function UploadFilePill({
  item,
  onRetry,
}: {
  item: TaskFileUploadItem;
  onRetry: () => void;
}) {
  const progressValue =
    item.status === "complete" ? 100 : Math.max(item.progress, 0);

  return (
    <div
      className={classNames(
        "w-56 rounded-md border bg-white shadow-sm overflow-hidden",
        item.status === "error" ? "border-red-400" : "border-gray-200"
      )}
    >
      <div className="h-1 w-full bg-gray-100">
        <div
          className={classNames(
            "h-full transition-all",
            item.status === "complete" ? "bg-persian-500" : "bg-persian-500"
          )}
          style={{ width: `${progressValue}%` }}
        />
      </div>
      <div className="flex items-center justify-between gap-x-2 p-2">
        <div className="flex items-center gap-x-2 min-w-0">
          <LocalFileIcon fileType={item.file.type} fileName={item.file.name} size="m" />
          <div className="min-w-0">
            <p className="text-xs font-medium text-gray-700 line-clamp-1">
              {item.name}
            </p>
            <p className="text-[10px] text-gray-400">
              {formatFileSize(item.file.size)}
            </p>
          </div>
        </div>
        <div className="flex items-center justify-end">
          {item.status === "uploading" || item.status === "creating" ? (
            <span className="text-[10px] text-gray-500">
              {progressValue}%
            </span>
          ) : item.status === "complete" ? (
            <CheckCircle2Icon className="w-4 h-4 text-persian-500" />
          ) : item.status === "error" ? (
            <button
              type="button"
              onClick={onRetry}
              className="text-[10px] text-red-600 font-semibold"
            >
              Retry
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function TaskFilePill({
  file,
  onClick,
  selectionMode,
  isSelected,
  isSelectable,
  onSelectionChange,
}: {
  file: TaskFile;
  onClick: () => void;
  selectionMode: boolean;
  isSelected: boolean;
  isSelectable: boolean;
  onSelectionChange: () => void;
}) {
  const canPreview = Boolean(file.viewUrl);
  const isReady = file.uploadStatus === TaskFileUploadStatus.Ready;
  const isPreparing =
    file.uploadStatus === TaskFileUploadStatus.Preparing ||
    file.uploadStatus === TaskFileUploadStatus.Pending;
  const isFailed = file.uploadStatus === TaskFileUploadStatus.Failed;

  return (
    <button
      type="button"
      onClick={() => {
        if (selectionMode) {
          if (isSelectable) {
            onSelectionChange();
          }
          return;
        }
        if (canPreview) {
          onClick();
        }
      }}
      className={classNames(
        "w-56 rounded-md border bg-white shadow-sm overflow-hidden text-left",
        canPreview ? "hover:border-gray-300" : "cursor-default",
        isFailed ? "border-red-400" : "border-gray-200"
      )}
    >
      <div className="h-1 w-full bg-gray-100">
        <div
          className={classNames(
            "h-full transition-all",
            isReady ? "bg-persian-500" : "bg-gray-200"
          )}
          style={{ width: isReady ? "100%" : "30%" }}
        />
      </div>
      <div className="flex items-center justify-between gap-x-2 p-2">
        <div className="flex items-center gap-x-2 min-w-0">
          <Checkbox
            checked={isSelected}
            disabled={!isSelectable}
            aria-label={`Select ${file.fileName}`}
            onChange={(event) => {
              event.stopPropagation();
              if (!isSelectable) {
                return;
              }
              onSelectionChange();
            }}
          />
          <LocalFileIcon fileType={file.fileType} fileName={file.fileName} size="m" />
          <div className="min-w-0">
            <p className="text-xs font-medium text-gray-700 line-clamp-1">
              {file.fileName}
            </p>
            <p className="text-[10px] text-gray-400">
              {formatFileSize(file.fileSize)}
            </p>
          </div>
        </div>
        <div className="flex items-center justify-end">
          {isPreparing ? (
            <Spinner color="gray" size="s" />
          ) : isReady ? (
            <CheckCircle2Icon className="w-4 h-4 text-persian-500" />
          ) : isFailed ? (
            <span className="text-[10px] text-red-600 font-semibold">Failed</span>
          ) : null}
        </div>
      </div>
    </button>
  );
}

function TaskFileSlideOver({
  file,
  open,
  onClose,
}: {
  file: TaskFile | null;
  open: boolean;
  onClose: () => void;
}) {
  return (
    <SlideOver open={open} onClose={onClose} maxWidth="max-w-4xl">
      <div className="h-full flex flex-col">
        <div className="border-b border-gray-200 shadow-sm p-4 flex justify-between items-center gap-x-2">
          <div className="flex items-center gap-x-2">
            <PanelRightCloseIcon
              onClick={onClose}
              className="w-4 h-4 text-gray-500 hover:text-gray-700 cursor-pointer"
            />
            <LocalFileIcon fileName={file?.fileName ?? ""} fileType={file?.fileType ?? FileType.Other} />
            <p className="text-sm text-gray-800 font-semibold">
              {file?.fileName ?? "File"}
            </p>
          </div>
          {file?.downloadUrl ? (
            <Button
              variant="neutral"
              size="s"
              text="Download"
              icon={DownloadIcon}
              onClick={() => {
                if (!file?.downloadUrl) {
                  return;
                }
                const a = document.createElement("a");
                a.href = file.downloadUrl;
                a.target = "_blank";
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
              }}
            />
          ) : null}
        </div>
        <div className="flex-1 min-h-0">
          {file ? (
            <TaskFileViewer file={file} />
          ) : (
            <div className="h-full w-full flex items-center justify-center">
              <Spinner color="gray" size="s" />
              <p className="text-sm text-gray-500 ml-2">Loading file...</p>
            </div>
          )}
        </div>
      </div>
    </SlideOver>
  );
}

function isSyncfusionPreviewable(mimeType: string) {
  // Check common MIME types that Syncfusion previewers support
  const supportedMimeTypes = [
    "application/pdf",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
    "application/vnd.openxmlformats-officedocument.presentationml.presentation", // .pptx
  ];
  return supportedMimeTypes.includes(mimeType.toLowerCase());
}

function TaskFileViewer({ file }: { file: TaskFile }) {
  if (!file.viewUrl) {
    return (
      <div className="h-full w-full flex items-center justify-center">
        <p className="text-sm text-gray-500">Preview not available.</p>
      </div>
    );
  }

  if (isSyncfusionPreviewable(file.fileType)) {
    return (
      <div className="h-full w-full min-h-0 overflow-hidden">
        <SyncfusionPdfViewer url={file.viewUrl} height="100%" />
      </div>
    );
  }

  return (
    <div className="h-full w-full">
      <DocViewer
        key={file.viewUrl}
        config={{
          pdfVerticalScrollByDefault: true,
          header: {
            disableFileName: true,
            disableHeader: true,
          },
        }}
        prefetchMethod="GET"
        style={{ height: "100%", width: "100%" }}
        documents={[
          {
            uri: file.viewUrl,
          },
        ]}
        pluginRenderers={DocViewerRenderers}
      />
    </div>
  );
}

function AddTaskFilesToDataRoomModal({
  open,
  onClose,
  selectedFiles,
  onCompleted,
}: {
  open: boolean;
  onClose: () => void;
  selectedFiles: TaskFile[];
  onCompleted: () => void;
}) {
  const client = useGqlClient();
  const { dealId } = useParams<{ dealId: string }>();
  const [selectedFolder, setSelectedFolder] = useState<DataRoomFolder | null>(
    null
  );
  const [selectedPermission, setSelectedPermission] = useState<Option | null>(
    null
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const dataRoomQuery = useDataRoomQuery(
    client,
    { dealId },
    { enabled: Boolean(dealId) }
  );

  const permissions = dataRoomQuery.data?.deal.dataRoom.permissions ?? [];

  useEffect(() => {
    if (!open) {
      return;
    }
    setSelectedFolder(null);
    setSelectedPermission(null);
    setIsSubmitting(false);
  }, [open]);

  useEffect(() => {
    if (!selectedPermission && permissions.length > 0) {
      setSelectedPermission({
        value: permissions[0].id,
        label: permissions[0].name,
        description: permissions[0].description,
      });
    }
  }, [permissions, selectedPermission]);

  const handleSubmit = async () => {
    if (!dealId) {
      toasts.error("Missing deal ID");
      return;
    }
    if (!selectedFolder || !selectedPermission || !selectedPermission.value) {
      return;
    }
    if (selectedFiles.length === 0) {
      toasts.error("Select at least one file");
      return;
    }

    setIsSubmitting(true);
    try {
      await client.request<
        { addTaskFilesToDataRoom: Array<{ id: string }> },
        {
          dealID: string;
          dataRoomFolderID: string;
          dataRoomPermissionID: string;
          taskFileIDs: string[];
        }
      >(AddTaskFilesToDataRoomDocument, {
        dealID: dealId,
        dataRoomFolderID: selectedFolder.id,
        dataRoomPermissionID: selectedPermission.value,
        taskFileIDs: selectedFiles.map((file) => file.id),
      });

      toasts.success("Added to Data Room");
      onCompleted();
    } catch (error) {
      toasts.error("Failed to add files to Data Room");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatedModal size="md" open={open} onClose={onClose}>
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">
          Add to Data Room
        </h3>
        <CloseIcon onClose={onClose} />
      </div>
      <div className="mt-4 space-y-4">
        <div>
          <div className="max-h-40 overflow-y-auto">
            {selectedFiles.length === 0 ? (
              <p className="text-sm text-gray-400">No files selected</p>
            ) : (
              <div className="space-y-2">
                {selectedFiles.map((file) => (
                  <div
                    key={file.id}
                    className="rounded-md border border-gray-200 bg-white p-2 flex items-center gap-x-2"
                  >
                    <LocalFileIcon
                      fileType={file.fileType}
                      fileName={file.fileName}
                      size="m"
                    />
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-gray-700 line-clamp-1">
                        {file.fileName}
                      </p>
                      <p className="text-[10px] text-gray-400">
                        {formatFileSize(file.fileSize)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div>
          <p className="text-sm font-semibold text-gray-700">Select folder</p>
          <AddFolderPill
            onFolderSelected={(folder) => setSelectedFolder(folder)}
            selectedFolder={selectedFolder}
          />
        </div>

        <div>
          {dataRoomQuery.isLoading ? (
            <div className="flex items-center gap-x-2 text-sm text-gray-500">
              <Spinner color="gray" size="s" />
              Loading permissions...
            </div>
          ) : dataRoomQuery.error ? (
            <p className="text-sm text-red-500">
              Failed to load permissions.
            </p>
          ) : (
            <Dropdown
              label="Permission level"
              defaultOption={
                permissions.length
                  ? {
                    label: permissions[0].name,
                    description: permissions[0].description,
                    value: permissions[0].id,
                  }
                  : undefined
              }
              selectedOption={selectedPermission ?? undefined}
              onSelect={(option) => setSelectedPermission(option)}
              options={permissions.map((permission) => {
                return {
                  value: permission.id,
                  label: permission.name,
                  description: permission.description,
                };
              })}
            />
          )}
        </div>

        <div className="flex items-center justify-end gap-x-2 mt-12">
          <Button
            variant="neutral"
            text="Cancel"
            onClick={onClose}
          />
          <Button
            variant="positive"
            text="Add to Data Room"
            onClick={handleSubmit}
            isLoading={isSubmitting}
            isDisabled={!selectedFolder || !selectedPermission?.value}
          />
        </div>
      </div>
    </AnimatedModal>
  );
}

function EditTaskModal({
  open,
  onClose,
  taskId,
  initialTitle,
  initialDescription,
  onUpdated,
}: {
  open: boolean;
  onClose: () => void;
  taskId: string;
  initialTitle: string;
  initialDescription: string;
  onUpdated: () => void;
}) {
  const client = useGqlClient();
  const updateTask = useUpdateTaskMutation(client);
  const [title, setTitle] = useState(initialTitle);
  const [description, setDescription] = useState(initialDescription);
  const [titleError, setTitleError] = useState("");

  useEffect(() => {
    if (open) {
      setTitle(initialTitle);
      setDescription(initialDescription);
      setTitleError("");
    }
  }, [open, initialTitle, initialDescription]);

  const handleSave = () => {
    if (title.trim() === "") {
      setTitleError("Title is required");
      return;
    }

    if (title === initialTitle && description === initialDescription) {
      onClose();
      return;
    }

    updateTask.mutate(
      {
        input: {
          id: taskId,
          title: title.trim(),
          description,
        },
      },
      {
        onSuccess: () => {
          toasts.success("Task updated");
          onUpdated();
        },
        onError: () => {
          toasts.error("Failed to update task");
        },
      }
    );
  };

  return (
    <AnimatedModal size="lg" open={open} onClose={onClose}>
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Edit task</h3>
        <CloseIcon onClose={onClose} />
      </div>
      <div className="mt-5 space-y-4">
        <TextInput
          label="Title"
          value={title}
          onChange={(e) => {
            setTitleError("");
            setTitle(e.currentTarget.value);
          }}
          error={titleError}
        />
        <div>
          <label className="text-sm font-semibold text-gray-700 mb-2 block">
            Description
          </label>
          <TextArea
            rows={5}
            name="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Add a description..."
          />
        </div>
        <div className="flex items-center justify-end gap-x-2">
          <Button
            variant="neutral"
            size="s"
            text="Cancel"
            onClick={onClose}
          />
          <Button
            variant="positive"
            size="s"
            text="Save"
            onClick={handleSave}
            isLoading={updateTask.isPending}
          />
        </div>
      </div>
    </AnimatedModal>
  );
}

function SubTasksSection({
  dealId,
  taskId,
  subTasks,
  onUpdated,
}: {
  dealId: string;
  taskId: string;
  subTasks: Task[];
  onUpdated: () => void;
}) {
  const client = useGqlClient();
  const createTask = useCreateTaskMutation(client);
  const [isCreating, setIsCreating] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [assignees, setAssignees] = useState<
    Pick<Account, "id" | "name" | "avatar" | "avatarColor">[]
  >([]);
  const [priority, setPriority] = useState<TaskPriority | null>(null);

  const completedCount = subTasks.filter(
    (task) => task.status === TaskStatus.Completed
  ).length;
  const totalCount = subTasks.length;
  const completionRatio = totalCount === 0 ? 0 : completedCount / totalCount;

  const handleCreate = () => {
    if (title.trim() === "") {
      toasts.error("Title cannot be empty");
      return;
    }

    if (!dealId) {
      toasts.error("Missing deal ID");
      return;
    }

    createTask.mutate(
      {
        input: {
          title: title.trim(),
          description,
          assignedTo: assignees.length ? assignees.map((a) => a.id) : undefined,
          priority: priority ?? TaskPriority.Low,
          parentTaskID: taskId,
          dealID: dealId,
        },
      },
      {
        onSuccess: () => {
          onUpdated();
          setTitle("");
          setDescription("");
          setAssignees([]);
          setPriority(null);
          setIsCreating(false);
          toasts.success("Subtask created");
        },
        onError: () => {
          toasts.error("Failed to create subtask");
        },
      }
    );
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && event.metaKey && title.trim() !== "") {
      event.preventDefault();
      handleCreate();
    }
  };

  return (
    <div className="mt-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-x-2">
          <h3 className="text-sm font-semibold text-gray-700">Subtasks</h3>
          <div className="flex items-center gap-x-2 text-xs text-gray-500">
            <ProgressRing ratio={completionRatio} />
            <span>
              {completedCount}/{totalCount}
            </span>
          </div>
        </div>
        <button
          onClick={() => {
            if (!isCreating) setIsCreating(true);
          }}
          aria-disabled={isCreating}
          className="flex items-center gap-x-1.5 text-xs font-medium text-gray-600 hover:text-gray-800"
        >
          <PlusIcon className="w-3.5 h-3.5" />
          Add subtask
        </button>
      </div>

      <div className="mt-3 space-y-1">
        {subTasks.length === 0 ? (
          <p className="text-sm text-gray-400">No subtasks yet</p>
        ) : (
          [...subTasks]
            .sort((a, b) => a.createdAt - b.createdAt)
            .map((subTask) => (
              <SubTaskRow
                key={subTask.id}
                dealId={dealId}
                subTask={subTask}
                onUpdated={onUpdated}
              />
            ))
        )}
      </div>

      {isCreating && (
        <div className="mt-4 rounded-lg border border-gray-200 bg-white p-3 overflow-visible relative">
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Subtask title"
            className="w-full text-sm font-medium text-gray-900 placeholder:text-gray-400 border-none focus:ring-0 p-0"
            autoFocus
          />
          <div className="mt-3">
            <TextArea
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Add a description..."
              name="subtask-description"
            />
          </div>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2">
              {assignees.map((assignee) => (
                <Pills key={assignee.id}>
                  <UserPill
                    account={assignee}
                    onRemove={() =>
                      setAssignees(
                        assignees.filter((a) => a.id !== assignee.id)
                      )
                    }
                  />
                </Pills>
              ))}
              <Pills>
                <SelectUserPill
                  excludedAccountIds={assignees.map((a) => a.id)}
                  icon={<UserPlusIcon className="w-4 h-4 text-gray-400" />}
                  title="Add assignee"
                  usePortal
                  onUserSelected={(acc) =>
                    setAssignees([...assignees, acc as Account])
                  }
                />
              </Pills>
              <Pills>
                <SelectTaskPriority
                  priority={priority ?? undefined}
                  onPriorityChanged={setPriority}
                />
              </Pills>
            </div>
            <div className="flex items-center gap-x-2 ml-auto">
              <Button
                variant="neutral"
                size="s"
                text="Cancel"
                onClick={() => {
                  setIsCreating(false);
                  setTitle("");
                  setDescription("");
                  setAssignees([]);
                  setPriority(null);
                }}
              />
              <Button
                variant="positive"
                size="s"
                text="Create"
                onClick={handleCreate}
                isLoading={createTask.isPending}
                isDisabled={title.trim() === ""}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SubTaskRow({
  dealId,
  subTask,
  onUpdated,
}: {
  dealId: string;
  subTask: Task;
  onUpdated: () => void;
}) {
  const client = useGqlClient();
  const updateTask = useUpdateTaskMutation(client);
  const isCompleted = subTask.status === TaskStatus.Completed;

  const handleToggle = () => {
    updateTask.mutate(
      {
        input: {
          id: subTask.id,
          status: isCompleted ? TaskStatus.Todo : TaskStatus.Completed,
        },
      },
      {
        onSuccess: onUpdated,
        onError: () => toasts.error("Failed to update subtask"),
      }
    );
  };

  return (
    <div className="flex items-center gap-x-3 rounded-lg border border-gray-100 bg-white px-3 py-2">
      <input
        type="checkbox"
        checked={isCompleted}
        onChange={handleToggle}
        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
      />
      <TaskPriorityIcon priority={subTask.priority ?? undefined} />
      <NavLink
        to={`/deal/${dealId}/tasks/${subTask.id}`}
        className={classNames(
          "text-sm flex-1 min-w-0 truncate",
          isCompleted ? "text-gray-400 line-through" : "text-gray-800"
        )}
      >
        {subTask.title}
      </NavLink>
      {subTask.assignedTo?.length ? (
        <div className="flex -space-x-1.5">
          {subTask.assignedTo.slice(0, 3).map((assignee) => (
            <Avatar key={assignee.id} account={assignee} size="xs" />
          ))}
          {subTask.assignedTo.length > 3 && (
            <span className="flex items-center justify-center w-5 h-5 text-xs font-medium bg-gray-200 text-gray-600 rounded-full ring-2 ring-white">
              +{subTask.assignedTo.length - 3}
            </span>
          )}
        </div>
      ) : (
        <span className="text-xs text-gray-400">Unassigned</span>
      )}
    </div>
  );
}

function ProgressRing({ ratio }: { ratio: number }) {
  const normalizedRatio = Math.min(Math.max(ratio, 0), 1);
  const radius = 6;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - normalizedRatio * circumference;

  return (
    <svg width="16" height="16" className="text-gray-300">
      <circle
        cx="8"
        cy="8"
        r={radius}
        stroke="currentColor"
        strokeWidth="2"
        fill="none"
      />
      <circle
        cx="8"
        cy="8"
        r={radius}
        stroke="#4f46e5"
        strokeWidth="2"
        fill="none"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        transform="rotate(-90 8 8)"
      />
    </svg>
  );
}

function PropertiesPanel({ task, onUpdated }: { task: TaskQuery['task']; onUpdated: () => void }) {
  const client = useGqlClient();
  const updateTask = useUpdateTaskMutation(client);
  const [localAssignees, setLocalAssignees] = useState(task.assignedTo ?? []);

  useEffect(() => {
    setLocalAssignees(task.assignedTo ?? []);
  }, [task.id, task.assignedTo]);

  const handleStatusChange = (newStatus: TaskStatus) => {
    updateTask.mutate(
      { input: { id: task.id, status: newStatus } },
      {
        onSuccess: onUpdated,
        onError: () => toasts.error("Failed to update status"),
      }
    );
  };

  const handlePriorityChange = (newPriority: TaskPriority) => {
    updateTask.mutate(
      { input: { id: task.id, priority: newPriority } },
      {
        onSuccess: onUpdated,
        onError: () => toasts.error("Failed to update priority"),
      }
    );
  };

  const handleAddAssignee = (account: Account) => {
    const newAssignees = [...localAssignees, account];
    setLocalAssignees(newAssignees);
    updateTask.mutate(
      { input: { id: task.id, assignedTo: newAssignees.map((a) => a.id) } },
      {
        onSuccess: onUpdated,
        onError: () => {
          toasts.error("Failed to assign user");
          setLocalAssignees(task.assignedTo ?? []);
        },
      }
    );
  };

  const handleRemoveAssignee = (accountId: string) => {
    const newAssignees = localAssignees.filter((a) => a.id !== accountId);
    setLocalAssignees(newAssignees);
    updateTask.mutate(
      { input: { id: task.id, assignedTo: newAssignees.map((a) => a.id) } },
      {
        onSuccess: onUpdated,
        onError: () => {
          toasts.error("Failed to remove assignee");
          setLocalAssignees(task.assignedTo ?? []);
        },
      }
    );
  };

  const handleDueDateChange = (date: Date) => {
    updateTask.mutate(
      { input: { id: task.id, dueDate: getUnixTime(date) } },
      {
        onSuccess: onUpdated,
        onError: () => toasts.error("Failed to update due date"),
      }
    );
  };

  return (
    <div className="space-y-5">
      {/* Status */}
      <div>
        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 block">
          Status
        </label>
        <div className="flex gap-1">
          <StatusButton
            status={TaskStatus.Todo}
            currentStatus={task.status}
            onClick={() => handleStatusChange(TaskStatus.Todo)}
          />
          <StatusButton
            status={TaskStatus.InProgress}
            currentStatus={task.status}
            onClick={() => handleStatusChange(TaskStatus.InProgress)}
          />
          <StatusButton
            status={TaskStatus.Completed}
            currentStatus={task.status}
            onClick={() => handleStatusChange(TaskStatus.Completed)}
          />
        </div>
      </div>

      {/* Assignees */}
      <div>
        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 block">
          Assignees
        </label>
        <div className="flex flex-wrap gap-2">
          {localAssignees.map((assignee) => (
            <Pills key={assignee.id}>
              <UserPill
                account={assignee}
                onRemove={() => handleRemoveAssignee(assignee.id)}
              />
            </Pills>
          ))}
          <Pills>
            <SelectUserPill
              usePortal
              excludedAccountIds={localAssignees.map((a) => a.id)}
              icon={<UserPlusIcon className="w-4 h-4 text-gray-400" />}
              title="Add assignee"
              onUserSelected={(acc) => {
                if (!acc) {
                  return;
                }

                handleAddAssignee(acc as Account)
              }
              }
            />
          </Pills>
        </div>
      </div>

      {/* Priority */}
      <div>
        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 block">
          Priority
        </label>
        <Pills>
          <SelectTaskPriority
            usePortal
            priority={task.priority ?? undefined}
            onPriorityChanged={handlePriorityChange}
          />
        </Pills>
      </div>

      {/* Due Date */}
      <div>
        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 block">
          Due date
        </label>
        <Pills>
          <DatePicker
            value={task.dueDate ? fromUnixTime(task.dueDate) : undefined}
            onChange={handleDueDateChange}
            pretext="Due"
            onRemove={() => {
              updateTask.mutate(
                { input: { id: task.id, dueDate: 0 } },
                { onSuccess: onUpdated }
              );
            }}
          />
        </Pills>
      </div>

      <div className="mt-8">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">Activity</h3>
        <ActivityTimeline
          activities={task.activity.filter(
            (a) => isSidebarTaskActivity(a.type)
          )}
          defaultSortOrder="desc"
        />
      </div>
    </div>
  );
}

function StatusButton({
  status,
  currentStatus,
  onClick,
}: {
  status: TaskStatus;
  currentStatus: TaskStatus;
  onClick: () => void;
}) {
  const isActive = status === currentStatus;

  const config = {
    [TaskStatus.Todo]: {
      icon: <CircleDashedIcon className="w-4 h-4" />,
      label: "Todo",
      activeClass: "bg-gray-100 text-gray-700 border-gray-300",
    },
    [TaskStatus.InProgress]: {
      icon: <CircleIcon className="w-4 h-4" />,
      label: "In Progress",
      activeClass: "bg-blue-50 text-blue-700 border-blue-300",
    },
    [TaskStatus.Completed]: {
      icon: <CheckCircle2Icon className="w-4 h-4" />,
      label: "Done",
      activeClass: "bg-green-50 text-green-700 border-green-300",
    },
    [TaskStatus.Cancelled]: {
      icon: <CircleDashedIcon className="w-4 h-4" />,
      label: "Cancelled",
      activeClass: "bg-red-50 text-red-700 border-red-300",
    },
  };

  const { icon, label, activeClass } = config[status];

  return (
    <button
      onClick={onClick}
      className={classNames(
        "flex items-center gap-x-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium border transition-colors",
        isActive ? activeClass : "bg-white text-gray-500 border-gray-200 hover:bg-gray-50"
      )}
    >
      {icon}
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}

function ActivityTimeline({
  activities,
  defaultSortOrder = "desc",
}: {
  activities: TaskQuery["task"]["activity"];
  defaultSortOrder?: "asc" | "desc";
}) {
  const sortedActivities = [...activities].sort((a, b) =>
    defaultSortOrder === "asc"
      ? a.createdAt - b.createdAt
      : b.createdAt - a.createdAt
  );

  if (sortedActivities.length === 0) {
    return (
      <p className="text-sm text-gray-400 py-4">No activity yet</p>
    );
  }

  return (
    <ActivityItems
      items={sortedActivities as unknown as DealActivityFragmentFragment[]}
      mode="compact"
      margin="0 0 0 0"
    />
  );
}
